import { ReactNode, useEffect, useMemo, useState } from "react";

import PlayerCell from "../components/PlayerCell";
import { useReport } from "../context/ReportContext";

/**
 * Combined Enchants + Gems audit. Reads `enchants` and `gems` metric
 * blocks from the report context and renders them as two side-by-side
 * cards. Each row shows a player's issues inline as colored chips
 * grouped by gear slot. Click a row to open a modal with the full
 * per-slot detail.
 *
 * Chip format: `<Slot>` for one issue, `<Slot> ×N` for multiple
 * (e.g. two uncommon gems on chest renders as "Chest ×2"). The chips
 * give the at-a-glance summary; the modal gives the full breakdown.
 */

type EnchantStatus = "missing" | "suboptimal" | "optimal" | "unknown";
type GemStatus = "empty" | "uncommon" | "rare" | "unknown";

interface EnchantSlotItem {
  slot: string;
  slotIndex: number;
  itemName: string;
  enchantId: number;
  enchantName?: string | null;
  status: EnchantStatus;
}

interface EnchantPlayerRow {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  specConfigured?: boolean;
  missingCount: number;
  suboptimalCount: number;
  optimalCount: number;
  unknownCount: number;
  issueCount: number;
  items: EnchantSlotItem[];
}

interface GemSlotItem {
  slot: string;
  slotIndex: number;
  socketIndex: number;
  itemName: string;
  gemId: number;
  gemItemLevel?: number | null;
  status: GemStatus;
}

interface GemPlayerRow {
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  emptyCount: number;
  uncommonCount: number;
  rareCount: number;
  unknownCount: number;
  issueCount: number;
  items: GemSlotItem[];
}

interface ChipGroup {
  slot: string;
  count: number;
}

const CHIP_RED = "bg-red-500/15 text-red-300 border border-red-500/20";
const CHIP_AMBER = "bg-amber-500/15 text-amber-300 border border-amber-500/20";

/** Sort slots in a sensible top-to-bottom-of-character order. */
const SLOT_RANK: Record<string, number> = {
  Head: 0,
  Neck: 1,
  Shoulder: 2,
  Back: 3,
  Chest: 4,
  Waist: 5,
  Wrist: 6,
  Hands: 7,
  "Finger 1": 8,
  "Finger 2": 9,
  Legs: 10,
  Feet: 11,
  "Trinket 1": 12,
  "Trinket 2": 13,
  "Main Hand": 14,
  "Off Hand": 15,
  Ranged: 16,
};

function groupBySlot<T extends { slot: string }>(items: T[]): ChipGroup[] {
  const map = new Map<string, ChipGroup>();
  for (const it of items) {
    const g = map.get(it.slot) ?? { slot: it.slot, count: 0 };
    g.count += 1;
    map.set(it.slot, g);
  }
  return [...map.values()].sort(
    (a, b) => (SLOT_RANK[a.slot] ?? 99) - (SLOT_RANK[b.slot] ?? 99),
  );
}

function Chip({ group, tone }: { group: ChipGroup; tone: "red" | "amber" }) {
  const className =
    "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums " +
    (tone === "red" ? CHIP_RED : CHIP_AMBER);
  return (
    <span className={className}>
      <span>{group.slot}</span>
      {group.count > 1 && (
        <span className="text-[10px] opacity-80">×{group.count}</span>
      )}
    </span>
  );
}

function ChipRow({
  groups,
  tone,
}: {
  groups: ChipGroup[];
  tone: "red" | "amber";
}) {
  if (groups.length === 0) {
    return <span className="text-gray-700">-</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {groups.map((g) => (
        <Chip key={g.slot} group={g} tone={tone} />
      ))}
    </div>
  );
}

type AuditKind = "enchants" | "gems";

interface AuditRow {
  key: string;
  name?: string;
  type?: string;
  icon?: string;
  spec?: string;
  specConfigured?: boolean;
  redGroups: ChipGroup[];
  amberGroups: ChipGroup[];
  hasIssues: boolean;
  totalIssues: number;
  /** Raw issue items used by the modal - preserves all per-slot detail. */
  redItems: EnchantSlotItem[] | GemSlotItem[];
  amberItems: EnchantSlotItem[] | GemSlotItem[];
}

interface AuditTableProps {
  caption: string;
  redLabel: string;
  amberLabel: string;
  rows: AuditRow[];
  emptyOK: string;
  onRowClick: (row: AuditRow) => void;
}

function AuditTable({
  caption,
  redLabel,
  amberLabel,
  rows,
  emptyOK,
  onRowClick,
}: AuditTableProps) {
  const visible = rows.filter((r) => r.hasIssues);

  return (
    <section className="min-w-0 rounded border border-border/60 bg-bg/40 px-4 py-3">
      <header className="mb-2">
        <h3 className="text-sm font-semibold tracking-tight text-gray-100">
          {caption}
        </h3>
      </header>
      {!rows.length ? (
        <p className="text-sm text-gray-500">No data - re-import the report.</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-emerald-300/80">{emptyOK}</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="pb-2 pr-3 font-medium">Player</th>
              <th className="pb-2 pr-3 font-medium">{redLabel}</th>
              <th className="pb-2 font-medium">{amberLabel}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr
                key={r.key}
                onClick={() => onRowClick(r)}
                className="border-t border-border/40 cursor-pointer hover:bg-white/[0.04] transition-colors"
              >
                <td className="py-2 pr-3 align-top">
                  <PlayerCell name={r.name} type={r.type} icon={r.icon} />
                  {r.specConfigured === false && (
                    <span
                      className="ml-1.5 text-[9px] uppercase tracking-wider text-gray-600"
                      title="No optimal-enchant list configured for this spec; only missing enchants are flagged."
                    >
                      unconfigured
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 align-top">
                  <ChipRow groups={r.redGroups} tone="red" />
                </td>
                <td className="py-2 align-top">
                  <ChipRow groups={r.amberGroups} tone="amber" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* --------------------------- Modal ---------------------------------- */

function Modal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  // Close on Escape, and lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-lg border border-border bg-panel shadow-2xl flex flex-col"
      >
        {children}
      </div>
    </div>
  );
}

const STATUS_PILL: Record<EnchantStatus | GemStatus, string> = {
  missing: "bg-red-500/15 text-red-300",
  suboptimal: "bg-amber-500/15 text-amber-300",
  optimal: "bg-emerald-500/15 text-emerald-300",
  empty: "bg-red-500/15 text-red-300",
  uncommon: "bg-amber-500/15 text-amber-300",
  rare: "bg-emerald-500/15 text-emerald-300",
  unknown: "bg-gray-500/15 text-gray-400",
};

const STATUS_LABEL: Record<EnchantStatus | GemStatus, string> = {
  missing: "Missing",
  suboptimal: "Suboptimal",
  optimal: "Optimal",
  empty: "Empty",
  uncommon: "Suboptimal",
  rare: "Rare",
  unknown: "Unknown",
};

function isGemItem(it: EnchantSlotItem | GemSlotItem): it is GemSlotItem {
  return "gemId" in it;
}

/** Sort issue items so the modal table mirrors the character sheet order. */
function sortIssueItems<T extends EnchantSlotItem | GemSlotItem>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    const rankA = SLOT_RANK[a.slot] ?? 99;
    const rankB = SLOT_RANK[b.slot] ?? 99;
    if (rankA !== rankB) return rankA - rankB;
    // For gems, multiple sockets share a slot - keep socket order stable.
    if (isGemItem(a) && isGemItem(b)) return a.socketIndex - b.socketIndex;
    return 0;
  });
}

function PlayerDetailModal({
  row,
  kind,
  onClose,
}: {
  row: AuditRow;
  kind: AuditKind;
  onClose: () => void;
}) {
  const items = sortIssueItems([
    ...(row.redItems as (EnchantSlotItem | GemSlotItem)[]),
    ...(row.amberItems as (EnchantSlotItem | GemSlotItem)[]),
  ]);

  const detailLabel = kind === "enchants" ? "Enchant" : "Gem";

  return (
    <Modal open onClose={onClose}>
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="space-y-1 min-w-0">
          <PlayerCell name={row.name} type={row.type} icon={row.icon} />
          <p className="text-xs text-gray-500">
            {kind === "enchants" ? "Enchant" : "Gem"} issues
            {row.spec ? ` · ${row.spec}` : ""}
          </p>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-white/5 hover:text-gray-100"
        >
          <CloseIcon />
        </button>
      </header>
      <div className="overflow-y-auto px-4 py-3">
        {items.length === 0 ? (
          <p className="text-sm text-gray-500">No issues for this player.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
                <th className="pb-2 pr-3 font-medium">Slot</th>
                <th className="pb-2 pr-3 font-medium">Item</th>
                <th className="pb-2 pr-3 font-medium">{detailLabel}</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr
                  key={`${it.slotIndex}-${
                    isGemItem(it) ? it.socketIndex : "ench"
                  }-${i}`}
                  className="border-t border-border/40"
                >
                  <td className="py-1.5 pr-3 text-gray-400 w-24">{it.slot}</td>
                  <td className="py-1.5 pr-3 text-gray-300 truncate max-w-[16rem]">
                    {it.itemName || "-"}
                  </td>
                  <td className="py-1.5 pr-3 text-gray-300">
                    {isGemItem(it) ? (
                      it.status === "empty" ? (
                        <span className="text-gray-600 italic">
                          (empty socket)
                        </span>
                      ) : (
                        <span title={`gem id ${it.gemId}`}>
                          #{it.gemId}
                          {it.gemItemLevel ? (
                            <span className="ml-1 text-gray-500">
                              ilvl {it.gemItemLevel}
                            </span>
                          ) : null}
                        </span>
                      )
                    ) : it.status === "missing" ? (
                      <span className="text-gray-600 italic">none</span>
                    ) : (
                      <span title={`enchant id ${it.enchantId}`}>
                        {it.enchantName ?? `#${it.enchantId}`}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5">
                    <span
                      className={
                        "inline-block rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-medium " +
                        STATUS_PILL[it.status]
                      }
                    >
                      {STATUS_LABEL[it.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

/* --------------------------- Main view ------------------------------ */

export default function EnchantsGems() {
  const { report } = useReport();
  const [selected, setSelected] = useState<{
    kind: AuditKind;
    row: AuditRow;
  } | null>(null);

  const enchantsBlock = report?.metrics?.enchants;
  const gemsBlock = report?.metrics?.gems;

  const enchantRows: AuditRow[] = useMemo(() => {
    const rows = (enchantsBlock?.rows ?? []) as unknown as EnchantPlayerRow[];
    return rows
      .map((r): AuditRow => {
        const missing = r.items.filter((it) => it.status === "missing");
        const subopt = r.items.filter((it) => it.status === "suboptimal");
        return {
          key: r.name ?? Math.random().toString(),
          name: r.name,
          type: r.type,
          icon: r.icon,
          spec: r.spec,
          specConfigured: r.specConfigured,
          redGroups: groupBySlot(missing),
          amberGroups: groupBySlot(subopt),
          redItems: missing,
          amberItems: subopt,
          hasIssues: r.issueCount > 0,
          totalIssues: r.missingCount * 10 + r.suboptimalCount,
        };
      })
      .sort(
        (a, b) =>
          b.totalIssues - a.totalIssues ||
          (a.name ?? "").localeCompare(b.name ?? ""),
      );
  }, [enchantsBlock]);

  const gemRows: AuditRow[] = useMemo(() => {
    const rows = (gemsBlock?.rows ?? []) as unknown as GemPlayerRow[];
    return rows
      .map((r): AuditRow => {
        const empty = r.items.filter((it) => it.status === "empty");
        const uncommon = r.items.filter((it) => it.status === "uncommon");
        return {
          key: r.name ?? Math.random().toString(),
          name: r.name,
          type: r.type,
          icon: r.icon,
          spec: r.spec,
          redGroups: groupBySlot(empty),
          amberGroups: groupBySlot(uncommon),
          redItems: empty,
          amberItems: uncommon,
          hasIssues: r.issueCount > 0,
          totalIssues: r.emptyCount * 10 + r.uncommonCount,
        };
      })
      .sort(
        (a, b) =>
          b.totalIssues - a.totalIssues ||
          (a.name ?? "").localeCompare(b.name ?? ""),
      );
  }, [gemsBlock]);

  if (!report) return null;

  const enchTotals = (enchantsBlock?.totals ?? {}) as {
    raidMissing?: number;
    raidSuboptimal?: number;
  };
  const gemTotals = (gemsBlock?.totals ?? {}) as {
    raidEmpty?: number;
    raidUncommon?: number;
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500 italic">
        Click a row for more details.
      </p>

      {/* Each grid cell pairs its own summary strip with the table
          beneath it, so on narrow screens the strip stays above its
          matching table when the columns collapse to one. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-6 items-start">
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="text-gray-400 font-medium uppercase tracking-wider text-[10px]">
              Enchants
            </span>
            <span className="tabular-nums">
              <span className="text-red-300 font-medium">
                {enchTotals.raidMissing ?? 0}
              </span>{" "}
              missing
            </span>
            <span className="tabular-nums">
              <span className="text-amber-300 font-medium">
                {enchTotals.raidSuboptimal ?? 0}
              </span>{" "}
              suboptimal
            </span>
          </div>
          <AuditTable
            caption="Enchants"
            redLabel="Missing"
            amberLabel="Suboptimal"
            rows={enchantRows}
            emptyOK="All checked players have clean enchants."
            onRowClick={(row) => setSelected({ kind: "enchants", row })}
          />
        </div>
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="text-gray-400 font-medium uppercase tracking-wider text-[10px]">
              Gems
            </span>
            <span className="tabular-nums">
              <span className="text-red-300 font-medium">
                {gemTotals.raidEmpty ?? 0}
              </span>{" "}
              empty
            </span>
            <span className="tabular-nums">
              <span className="text-amber-300 font-medium">
                {gemTotals.raidUncommon ?? 0}
              </span>{" "}
              suboptimal
            </span>
          </div>
          <AuditTable
            caption="Gems"
            redLabel="Empty"
            amberLabel="Suboptimal"
            rows={gemRows}
            emptyOK="All checked players have rare gems and no empty sockets."
            onRowClick={(row) => setSelected({ kind: "gems", row })}
          />
        </div>
      </div>

      {selected && (
        <PlayerDetailModal
          row={selected.row}
          kind={selected.kind}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
